// In dev mode webpack proxies /api requests to API_URL itself

import type { CreateAxiosDefaults, AxiosResponse, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { t } from 'i18next';
import { ConflictError, CustomError, ForbiddenError } from 'core/base';
import { URL_LIST } from 'core/store/modules/common/literals';
import merge from 'lodash/merge';
import { fusionAuthRedirect } from '../fusionAuthRedirect';
import { env } from '../../utils';

const defaultConfig = {
  baseURL: env.API_URL,
  headers: { Accept: 'application/json' },
  withCredentials: true,
  adapter: 'xhr',
};

export function createHttpClient(config?: CreateAxiosDefaults) {
  const httpClient = axios.create(merge(defaultConfig, config));
  type AxiosError = {
    config: AxiosRequestConfig;
    response: AxiosResponse;
    toString: () => string;
  };

  httpClient.interceptors.response.use(
    options => options,
    (error: AxiosError) => {
      const { response } = error;

      try {
        switch (response.status) {
          case 401: {
            // 401 Unauthorized
            fusionAuthRedirect(URL_LIST.auth);
            break;
          }

          case 409: {
            return Promise.reject(
              new ConflictError({}, response.statusText || response.data.message),
            );
          }

          case 403: {
            return Promise.reject(new ForbiddenError(response.data));
          }
          case 500: {
            return Promise.reject(
              new CustomError(t('Errors.Code500'), response.status, response.data),
            );
          }

          default: {
            return Promise.reject(
              new CustomError(response.statusText, response.status, response.data),
            );
          }
        }
      } catch (error_) {
        // eslint-disable-next-line no-console
        console.error(error_);
      }
      return Promise.reject(response);
    },
  );

  return httpClient;
}
